import { useAIRecommendation } from '@hakimo-ui/hakimo/data-access';
import { UnifiedMonitoringProduct } from '@hakimo-ui/hakimo/types';
import { useUser } from '@hakimo-ui/hakimo/util';
import clsx from 'clsx';
import ClickableTimestamp from './ClickableTimestamp';
import LoiteringDetails from './LoiteringRecommendation';
import RecommendationCard from './RecommendationCard';
import {
  createTimestampClickHandler,
  getRecommendationStyles,
  REFETCH_INTERVAL,
  RESOLVE_RECOMMENDATION,
  useAlarmGroupContext,
} from './utils';

interface Props {
  alarmId: string;
  product: UnifiedMonitoringProduct;
  timeZone: string;
  hideDetails?: boolean;
}

export function AIRecommendation({
  alarmId,
  product,
  timeZone,
  hideDetails = false,
}: Props) {
  const { data } = useAIRecommendation(alarmId, product, REFETCH_INTERVAL);
  const aiOperatorRep = data ? data[0] : undefined;
  const isLoitering = aiOperatorRep?.rawState?.type === 'loitering';
  const loiteringData = aiOperatorRep?.rawState?.cameras;
  const user = useUser();

  const alarmGroupContext = useAlarmGroupContext();
  const handleTimestampClick = createTimestampClickHandler(
    alarmGroupContext,
    user
  );

  const recommendationStyles = getRecommendationStyles();
  const escalatedStyles = getRecommendationStyles('deny');
  const resolvedStyles = getRecommendationStyles('approve');

  if (!aiOperatorRep?.recommendations) {
    return null;
  }

  const headlineStyles =
    aiOperatorRep.recommendations !== RESOLVE_RECOMMENDATION
      ? escalatedStyles
      : resolvedStyles;

  const isResolve = aiOperatorRep.recommendations === RESOLVE_RECOMMENDATION;

  const displayRecommendation = hideDetails
    ? 'ESCALATE'
    : aiOperatorRep.recommendations;

  return (
    <RecommendationCard
      compact={hideDetails}
      badge={{
        label: displayRecommendation,
        className: recommendationStyles.badgeClasses,
      }}
      headline={
        aiOperatorRep.headline
          ? {
              icon: headlineStyles.icon,
              text: aiOperatorRep.headline,
              className: clsx(
                isResolve
                  ? 'text-emerald-700 dark:text-emerald-400'
                  : 'text-alert-600'
              ),
            }
          : undefined
      }
      body={
        <>
          {!isLoitering && aiOperatorRep.summary && (
            <div className="scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 max-h-36 overflow-y-auto pr-2">
              <p className="text-[13px] leading-relaxed text-gray-600 dark:text-gray-400">
                <ClickableTimestamp
                  text={aiOperatorRep.summary}
                  onTimestampClick={handleTimestampClick}
                />
              </p>
            </div>
          )}

          {isLoitering && loiteringData?.length ? (
            <LoiteringDetails
              cameras={loiteringData}
              onTimestampClick={handleTimestampClick}
              timeZone={timeZone}
            />
          ) : null}
        </>
      }
      timeZone={timeZone}
    />
  );
}

export default AIRecommendation;
